import React, { useMemo } from 'react';

import {
  ALINE_DRAFT_DATA_ATTRIBUTES,
  createDocumentRoute,
  parseHtmlElementAttributes,
} from '@aline/common';
import { IconButton, Typography } from '@mui/material';
import { Copy, ExternalLink, FilePlus2 } from 'lucide-react';
import { useSnackbar } from 'notistack';
import { AlineTooltip, Spacer } from 'src/components';
import { ContractIcon } from 'src/components/icons/ContractIcon';
import { Apollo } from 'src/gql/generated';
import { openLink } from 'src/helpers/url';
import { useGetChatThread } from 'src/hooks';
import { HtmlPreview } from 'src/utils/HtmlPreview';
import { previewListStyles } from 'src/utils/preview-list-styles';
import { previewTableStyles } from 'src/utils/preview-styles';
import { makeStyles } from 'tss-react/mui';

import { useDrafterNode } from '../../../ChatPanel/PlanTree/SubAgentCards/DrafterNodeContext';
import type { MarkdownComponentProps } from '../../ChatPanelMarkdown';
import { useChatAssistantMessage } from '../../ChatPanelMarkdown';
import { getCodeBlockInfoFromNode } from '../../remark-plugins/remarkCodeBlock';

const useStyles = makeStyles()(theme => ({
  body: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
  },
  buttons: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.25),
  },
  deletedNotice: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    padding: theme.spacing(0.5, 2),
  },
  header: {
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    borderBottom: `1px solid ${theme.appColors.rule}`,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    display: 'flex',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  iconSuccess: {
    color: theme.palette.success.main,
  },
  iconWrapper: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'center',
  },
  preview: {
    ...previewListStyles(theme),
    ...previewTableStyles(theme, { cellPadding: 0.75 }),
    '& h1, & h2, & h3': {
      marginBottom: theme.spacing(1),
      marginTop: theme.spacing(2),
    },
    '& p': {
      marginBottom: theme.spacing(1),
      marginTop: 0,
    },
  },
  section: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(2, 0),
    position: 'relative',
  },
}));

type HtmlDraftPreview = {
  documentId: string;
  html: string;
  title: string;
};
type ThreadArtifactRow = NonNullable<
  Apollo.GetAgentThreadQuery['agentThread']
>['artifacts'][number];
type ThreadDocumentArtifactRow = Extract<
  ThreadArtifactRow,
  { __typename: 'ThreadDocumentArtifact' }
>;

export const CodeHtmlDraftElement = React.memo(function CodeHtmlDraftElement({
  node,
}: MarkdownComponentProps) {
  const { classes } = useStyles();
  const drafterNode = useDrafterNode();
  const { enqueueSnackbar } = useSnackbar();
  const message = useChatAssistantMessage();
  const codeBlockInfo = getCodeBlockInfoFromNode(node);
  const codeBlockCode = codeBlockInfo?.code ?? null;
  const { thread } = useGetChatThread(drafterNode?.threadId ?? null);
  const [recreate, { loading: recreateLoading }] =
    Apollo.useRecreateAgentArtifactDocumentMutation();

  const draft = useMemo(
    () => (codeBlockCode ? parseHtmlDraftPreview(codeBlockCode) : null),
    [codeBlockCode]
  );

  const draftedArtifact = useMemo(
    () =>
      (thread?.artifacts ?? []).find((artifact): artifact is ThreadDocumentArtifactRow =>
        getIsDraftedDocumentArtifact(artifact, draft?.documentId)
      ) ?? null,
    [draft, thread?.artifacts]
  );

  if (!codeBlockInfo || !draft) return null;

  const copyDisabledReason = codeBlockInfo.isNodeStreaming ? 'AI is writing...' : '';
  const hasDraftedArtifact = Boolean(draftedArtifact);
  const createDisabledReason = getCreateDisabledReason({
    drafterNodeExists: Boolean(drafterNode),
    hasDraftedArtifact,
    isNodeStreaming: codeBlockInfo.isNodeStreaming,
    recreateLoading,
  });
  const actionTooltip = hasDraftedArtifact
    ? 'Open document'
    : createDisabledReason || 'Create document';

  async function handleRecreate() {
    if (!drafterNode || !draft) return;

    try {
      await recreate({
        update: (cache, { data: result }) => {
          const newArtifact = result?.recreateAgentArtifactDocument;
          if (!newArtifact) return;
          cache.modify({
            fields: {
              artifacts(existing = []) {
                if (!Array.isArray(existing)) return existing;
                return [newArtifact, ...existing];
              },
            },
            id: cache.identify({ __typename: 'AgentThread', id: drafterNode.threadId }),
          });
        },
        variables: {
          documentId: draft.documentId,
          html: draft.html,
          messageId: message.id,
          title: draft.title,
        },
      });
      enqueueSnackbar('Document created.', { variant: 'success' });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      enqueueSnackbar(`Failed to recreate document: ${errorMsg}`, { variant: 'error' });
    }
  }

  async function handleCopy() {
    if (!draft) return;

    try {
      await writeHtmlToClipboard(draft.html);
      enqueueSnackbar('AI draft HTML copied to clipboard.', { variant: 'success' });
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : String(error);
      enqueueSnackbar(`Failed to copy AI draft HTML: ${errorMsg}`, { variant: 'error' });
    }
  }

  function handleDocumentAction() {
    if (draftedArtifact) {
      openLink(createDocumentRoute(draftedArtifact.document.id), {
        title: draftedArtifact.document.name,
      });
      return;
    }

    void handleRecreate();
  }

  return (
    <section className={classes.section}>
      <div className={classes.header}>
        <div className={classes.iconWrapper}>
          <ContractIcon />
        </div>
        <Typography variant="subtitle2">{draft.title}</Typography>
        <Spacer />
        <div className={classes.buttons}>
          <AlineTooltip
            arrow
            placement="top"
            title={copyDisabledReason || 'Copy AI draft to clipboard'}
          >
            <div>
              <IconButton
                disabled={recreateLoading || Boolean(copyDisabledReason)}
                size="small"
                onClick={handleCopy}
              >
                <Copy size={16} stroke="currentColor" strokeWidth={1.5} />
              </IconButton>
            </div>
          </AlineTooltip>
          {/**
           * Match the table / AI-message Create-document affordance:
           * same icon (`FilePlus2`), same right-of-copy position.
           * Switches to an open-in-new-window icon (`ExternalLink`) once a
           * `Drafted` artifact exists so the user can tell at a glance the
           * doc is already there.
           */}
          <AlineTooltip arrow placement="top" title={actionTooltip}>
            <div>
              <IconButton
                aria-label={hasDraftedArtifact ? 'Open document' : 'Create document'}
                disabled={Boolean(createDisabledReason)}
                size="small"
                onClick={handleDocumentAction}
              >
                {hasDraftedArtifact ? (
                  <ExternalLink size={16} stroke="currentColor" strokeWidth={1.5} />
                ) : (
                  <FilePlus2 size={16} stroke="currentColor" strokeWidth={1.5} />
                )}
              </IconButton>
            </div>
          </AlineTooltip>
        </div>
      </div>
      {!codeBlockInfo.isNodeStreaming && drafterNode && !hasDraftedArtifact && (
        <Typography align="center" className={classes.deletedNotice} variant="caption">
          This draft was deleted. Use the create document button to create a new document from the
          draft.
        </Typography>
      )}
      <div className={classes.body}>
        <HtmlPreview className={classes.preview} html={draft.html} />
      </div>
    </section>
  );
});

function getCreateDisabledReason(options: {
  drafterNodeExists: boolean;
  hasDraftedArtifact: boolean;
  isNodeStreaming: boolean;
  recreateLoading: boolean;
}) {
  if (options.hasDraftedArtifact) return '';
  if (options.isNodeStreaming) return 'AI is writing...';
  if (!options.drafterNodeExists) return 'Document cannot be created yet';
  if (options.recreateLoading) return 'Creating document...';

  return '';
}

function parseHtmlDraftPreview(code: string): HtmlDraftPreview | null {
  const openMatch = code.match(/<aline-draft-document\b([^>]*)>/i);
  if (!openMatch || openMatch.index == null) return null;

  const openEndIndex = openMatch.index + openMatch[0].length;
  const closeIndex = code.search(/<\/aline-draft-document>/i);
  const html = (
    closeIndex === -1 ? code.slice(openEndIndex) : code.slice(openEndIndex, closeIndex)
  ).trim();
  const attributes = parseHtmlElementAttributes('aline-draft-document', openMatch[1] ?? '');
  const documentId = attributes.get(ALINE_DRAFT_DATA_ATTRIBUTES.documentId);
  if (!documentId) return null;

  return {
    documentId,
    html,
    title: getTitle(attributes.get(ALINE_DRAFT_DATA_ATTRIBUTES.documentTitle)),
  };
}

function getTitle(attributeTitle: string | null | undefined) {
  return attributeTitle?.trim() || 'Untitled Document';
}

function getIsThreadDocumentArtifact(
  artifact: ThreadArtifactRow
): artifact is ThreadDocumentArtifactRow {
  return artifact.__typename === 'ThreadDocumentArtifact';
}

function getIsDraftedDocumentArtifact(
  artifact: ThreadArtifactRow,
  documentId: string | undefined
): artifact is ThreadDocumentArtifactRow {
  return (
    getIsThreadDocumentArtifact(artifact) &&
    artifact.kindList.includes(Apollo.AgentMessageJoinKind.Drafted) &&
    artifact.document.id === documentId
  );
}

async function writeHtmlToClipboard(html: string) {
  if (typeof ClipboardItem === 'undefined' || !navigator.clipboard.write) {
    await navigator.clipboard.writeText(html);
    return;
  }

  const clipboardItem = new ClipboardItem({
    'text/html': new Blob([html], { type: 'text/html' }),
    'text/plain': new Blob([html], { type: 'text/plain' }),
  });
  await navigator.clipboard.write([clipboardItem]);
}
